"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiCameraOffLine, RiCameraSwitchLine, RiCheckLine, RiCloseLine, RiContrast2Line, RiErrorWarningLine, RiFullscreenLine, RiGhostLine, RiGridLine, RiImageEditLine, RiImageLine, RiLayoutColumnLine, RiMore2Line, RiUser3Line } from "react-icons/ri";
import CameraZoomControl, { crossedPreset, hasTelephotoLens, zoomPresets } from "@/components/camera-zoom-control";
import FaceGuideOverlay from "@/components/face-guide-overlay";
import GhostReferencePicker from "@/components/ghost-reference-picker";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown";
import Slider from "@/components/ui/slider";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useBlackPhotoBackground } from "@/hooks/use-black-photo-background";
import { useDevicePreference } from "@/hooks/use-device-preference";
import { reportCameraInterrupted, reportCameraStartFailed } from "@/lib/camera-telemetry";
import { comparePoses, detectPose, faceGuideTargetPose, getFaceLandmarker, loadDetectableImage, poseInPane } from "@/lib/face-align";
import { PREVIEW_MAX_EDGE, computePersonMask, drawBackgroundMask, getPersonSegmenter, hasPerson } from "@/lib/photo-background";
import { backdropForNewPhoto, loadStudioSettings, previewFillForBackdrop } from "@/lib/photo-studio/studio-photo";
import { type PermissionDeniedToast, SAVE_PERMISSION_DENIED_TOAST, isPermissionDeniedError } from "@/lib/photo-upload-errors";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
interface CameraCaptureModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // `extras.studioMask` is set for Studio captures (see `studioCapture`): the
  // person cutout to store next to the untouched photo.
  onCapture: (image: Blob | File, extras?: {
    studioMask?: Blob;
  }) => void | Promise<void>;
  title?: string;
  children?: React.ReactNode;
  // Shown when onCapture is refused for permissions (a 403). Callers saving to
  // the gallery pass the Add Photos wording; the default doesn't name one.
  permissionDeniedToast?: PermissionDeniedToast;
  allowMultiple?: boolean; // If true, don't close modal after capture
  // Offer the Video button. Off by default: only callers whose onCapture saves
  // a video file (patient photos, note attachments) turn it on.
  allowVideo?: boolean;
  disabled?: boolean;
  ghostImageSrc?: string; // Optional reference image to ghost/onion-skin over the preview
  // When set, the ghost control lets the user pick a comparison photo from this
  // patient's gallery (in addition to uploading one from their device).
  patientId?: string;
  // Show the ghost/compare control. Off for surfaces with no before/after use
  // case (e.g. the standalone skin-analysis selfie tool).
  enableGhosting?: boolean;
  // Camera to open on: "environment" (rear, for photographing a patient) or
  // "user" (front, for selfie-style captures).
  defaultFacingMode?: "user" | "environment";
  // The caller saves to the patient's gallery and stores `extras.studioMask`,
  // so the camera offers Studio: the photo is saved as taken and the backdrop
  // is applied on top. Other callers (e.g. profile photos) get no Studio option.
  studioCapture?: boolean;
}

// How long a captured photo sits in the review tray before it is saved. Keep in
// sync with the `shrink-x` animation duration in tailwind.config.ts.
const PENDING_SHOT_DELAY_MS = 4000;
// How long a tap on a lens button takes to glide to its zoom.
const ZOOM_ANIMATION_MS = 280;
interface PendingShot {
  id: number;
  file: File;
  url: string;
  // Studio captures carry their cutout so it is saved with the photo.
  studioMask?: Blob;
}

// What the camera window shows when it can't show the camera: a short title,
// what to do about it, and, for a blocked camera, where to fix it on iOS.
interface CameraError {
  title: string;
  message: string;
  kind?: "camera" | "save";
  blocked?: boolean;
}

// iPads report themselves as Macs; a touch screen tells them apart.
function isAppleTouchDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}
function getTouchDistance(t1: React.Touch, t2: React.Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}
const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onCapture,
  title,
  children,
  permissionDeniedToast = SAVE_PERMISSION_DENIED_TOAST,
  allowMultiple = false,
  allowVideo = false,
  disabled = false,
  ghostImageSrc,
  patientId,
  enableGhosting = true,
  defaultFacingMode = "environment",
  studioCapture = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Microphone for video recording only, so a refused or busy mic never
  // blocks photos.
  const audioStreamRef = useRef<MediaStream | null>(null);
  // Bumped by every start and stop. A start whose getUserMedia resolves after
  // a newer start or a close stops its stream instead of keeping it: a stream
  // nobody holds keeps the camera on, and iOS then refuses the camera until
  // the app is restarted.
  const startRequestRef = useRef(0);
  // The facing mode (mobile) or deviceId (desktop) the open stream uses, so a
  // state change that already matches it doesn't reopen the camera.
  const activeCameraKeyRef = useRef<string | null>(null);
  const facingModeRef = useRef<"user" | "environment">(defaultFacingMode);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(defaultFacingMode);
  const [isFrontCamera, setIsFrontCamera] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startingRecordingRef = useRef(false);
  const [error, setError] = useState<CameraError | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Composition aids. Gridlines and the face guide are remembered on this
  // device, so staff don't have to turn them back on for every photo.
  const [showGrid, setShowGrid] = useDevicePreference("camera-show-grid", false);
  // Fill the whole screen with the camera instead of a centred window. Also
  // remembered on this device.
  const [fullScreen, setFullScreen] = useDevicePreference("camera-full-screen", false);
  const [showGhost, setShowGhost] = useState(false);
  // Object URL of the most recent capture in this session, used as the ghost
  // overlay so the user can line up a follow-up shot. Falls back to the
  // caller-provided ghostImageSrc when nothing has been captured yet.
  const [capturedGhostUrl, setCapturedGhostUrl] = useState<string | null>(null);
  // Ref mirror of capturedGhostUrl so we can revoke the object URL outside of
  // state-updater callbacks (which can run twice under StrictMode).
  const capturedGhostUrlRef = useRef<string | null>(null);
  // Bumped on every camera (re)start so an in-flight capture can tell whether
  // the stream/camera changed while toBlob was pending and skip resurrecting a
  // ghost from the previous camera.
  const captureGenerationRef = useRef(0);
  // CSS-fallback zoom baked into the captured ghost at capture time (1 when the
  // full frame was saved). Used to scale the ghost relative to the live zoom so
  // the onion-skin keeps lining up after the zoom changes.
  const [capturedGhostZoom, setCapturedGhostZoom] = useState(1);
  // User-picked reference image to ghost with (takes priority over the last
  // capture / the caller-provided ghostImageSrc). A full, unzoomed frame.
  const [selectedGhostUrl, setSelectedGhostUrl] = useState<string | null>(null);
  const selectedGhostUrlRef = useRef<string | null>(null);
  // Whether the "pick a comparison photo" chooser (gallery + upload) is open.
  const [showGhostPicker, setShowGhostPicker] = useState(false);
  // How the chosen comparison photo is shown: onion-skin "overlay" on the live
  // preview, "sideBySide" next to it (avoids washing out the live image), or
  // "hidden" (kept only as the alignment target).
  const [ghostViewMode, setGhostViewMode] = useState<"overlay" | "sideBySide" | "hidden">("overlay");
  // Opacity of the onion-skin overlay, in percent.
  const [ghostOpacity, setGhostOpacity] = useDevicePreference("camera-ghost-opacity", 60);
  // The strength slider sits between two menu items but isn't one, so the
  // menu's arrow-key navigation skips it. These refs let Up/Down step into and
  // out of it by hand.
  const overlayItemRef = useRef<HTMLDivElement>(null);
  const sideBySideItemRef = useRef<HTMLDivElement>(null);
  const ghostStrengthRef = useRef<HTMLDivElement>(null);
  const focusGhostStrength = (e: React.KeyboardEvent) => {
    const thumb = ghostStrengthRef.current?.querySelector<HTMLElement>('[role="slider"]');
    if (!thumb) return;
    e.preventDefault();
    thumb.focus();
  };
  // Blank line-art face guide (separate from the photo ghost) — a crisp
  // alignment outline that doesn't blur the live image.
  const [showFaceGuide, setShowFaceGuide] = useDevicePreference("camera-show-face-guide", false);
  // Whether the live preview's face lines up with the ghost/reference face, and
  // a short nudge when it doesn't.
  const [isAligned, setIsAligned] = useState(false);
  const [alignHint, setAlignHint] = useState<string | null>(null);

  // Studio is offered only to staff taking patient photos for the gallery. The
  // on/off choice is still stored as the user's `blackPhotoBackground`
  // preference, so staff who turned on the old black background get Studio.
  const {
    isAvailable: studioPreferenceAvailable,
    isOn: studioOn,
    setOn: setStudioOn
  } = useBlackPhotoBackground();
  const offerStudio = studioCapture && !!patientId && studioPreferenceAvailable;
  const studioMode = offerStudio && studioOn;
  const backgroundMaskRef = useRef<HTMLCanvasElement>(null);
  const [backgroundHint, setBackgroundHint] = useState<string | null>(null);

  // Resolved ghost source, in priority order. A captured ghost is already in
  // preview orientation + carries its capture-time zoom; a selected/prop image
  // is a full reference frame.
  const ghostUrl = selectedGhostUrl ?? capturedGhostUrl ?? ghostImageSrc ?? null;
  const ghostIsCaptured = !selectedGhostUrl && !!capturedGhostUrl;

  // Set/replace the session ghost from a freshly captured blob. bakedZoom is the
  // CSS-fallback zoom cropped into the blob (1 if the full frame was saved).
  const setGhostFromBlob = useCallback((blob: Blob, bakedZoom: number) => {
    if (capturedGhostUrlRef.current) {
      URL.revokeObjectURL(capturedGhostUrlRef.current);
    }
    const url = URL.createObjectURL(blob);
    capturedGhostUrlRef.current = url;
    setCapturedGhostUrl(url);
    setCapturedGhostZoom(bakedZoom);
  }, []);

  // Revoke and clear the session ghost.
  const clearGhost = useCallback(() => {
    if (capturedGhostUrlRef.current) {
      URL.revokeObjectURL(capturedGhostUrlRef.current);
      capturedGhostUrlRef.current = null;
    }
    setCapturedGhostUrl(null);
    setCapturedGhostZoom(1);
  }, []);

  // Revoke and clear the user-picked ghost image. Only object URLs (uploads)
  // need revoking; gallery URLs are plain remote URLs.
  const clearSelectedGhost = useCallback(() => {
    if (selectedGhostUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(selectedGhostUrlRef.current);
    }
    selectedGhostUrlRef.current = null;
    setSelectedGhostUrl(null);
  }, []);

  // Adopt a reference image (from the gallery or an upload) as the ghost.
  const applySelectedGhost = useCallback((url_0: string) => {
    if (selectedGhostUrlRef.current && selectedGhostUrlRef.current !== url_0 && selectedGhostUrlRef.current.startsWith("blob:")) {
      URL.revokeObjectURL(selectedGhostUrlRef.current);
    }
    selectedGhostUrlRef.current = url_0;
    setSelectedGhostUrl(url_0);
    setShowGhost(true); // reveal it immediately
    setShowGhostPicker(false);
  }, []);

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(10);
  const [isHardwareZoom, setIsHardwareZoom] = useState(false);
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const zoomIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref mirror of isHardwareZoom for synchronous checks inside async callbacks
  const isHardwareZoomRef = useRef(false);
  // Ref mirrors so gestures and animation frames read the latest zoom/range
  // without re-binding their handlers on every change.
  const zoomLevelRef = useRef(1);
  zoomLevelRef.current = zoomLevel;
  const zoomRangeRef = useRef({
    min: minZoom,
    max: maxZoom
  });
  zoomRangeRef.current = {
    min: minZoom,
    max: maxZoom
  };
  // Whether the device has a telephoto lens. With hardware zoom on the back
  // camera, Safari hands off to it automatically past its zoom, so it earns a
  // lens button like on iPhone.
  const hasTelephoto = hasTelephotoLens(devices);
  const zoomStops = useMemo(() => zoomPresets(minZoom, maxZoom, {
    telephoto: hasTelephoto && isHardwareZoom && !isFrontCamera
  }), [minZoom, maxZoom, hasTelephoto, isHardwareZoom, isFrontCamera]);
  const zoomStopsRef = useRef(zoomStops);
  zoomStopsRef.current = zoomStops;
  // Phones and tablets report which way the open camera faces. There, "Switch
  // camera" flips front/back like the iPhone instead of stepping through every
  // lens Safari lists as its own camera.
  const cameraReportsFacingRef = useRef(false);
  const switchByFacingRef = useRef(false);
  // Set once a front/back flip found no camera on the other side (a laptop's
  // built-in webcam reports "user" too); Switch then steps through devices.
  const facingSwitchUnavailableRef = useRef(false);
  const devicesRef = useRef(devices);
  devicesRef.current = devices;
  // Bumped on each front/back flip so the camera restarts even when the
  // requested side already matches the facingMode state (a desktop camera
  // that reports "user" opens while that state still reads "environment").
  const [facingSwitchCount, setFacingSwitchCount] = useState(0);
  const zoomAnimationRef = useRef<number | null>(null);
  // Hardware zoom is applied one constraint at a time; moves that arrive while
  // one is in flight collapse into the latest value so a fast pinch never
  // queues up a backlog of stale zoom steps.
  const hardwareZoomTargetRef = useRef<number | null>(null);
  const hardwareZoomBusyRef = useRef(false);

  // Pinch gesture refs
  const gestureStartRef = useRef<{
    distance: number;
    zoom: number;
  } | null>(null);
  const isGesturingRef = useRef(false);

  // Tap-to-focus state
  const [focusPoint, setFocusPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showFocusRing, setShowFocusRing] = useState(false);
  const focusRingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tapTimestampRef = useRef(0);
  const {
    isMobile
  } = useIsMobile();
  const {
    toast
  } = useToast();
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = setControlledOpen ?? setInternalOpen;
  // Ref mirror of isOpen so async capture callbacks can tell whether the modal
  // is still open before resurrecting session state (e.g. the ghost URL).
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // In multi-capture mode each photo waits in a tray (bottom right) for a few
  // seconds so it can be discarded before onCapture saves it, without blocking
  // the next shot.
  const [pendingShots, setPendingShots] = useState<PendingShot[]>([]);
  const pendingShotsRef = useRef<PendingShot[]>([]);
  const pendingTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const nextShotIdRef = useRef(0);
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  const removePendingShot = useCallback((id: number) => {
    const timer = pendingTimersRef.current.get(id);
    if (timer) clearTimeout(timer);
    pendingTimersRef.current.delete(id);
    const shot = pendingShotsRef.current.find(s => s.id === id);
    if (shot) URL.revokeObjectURL(shot.url);
    pendingShotsRef.current = pendingShotsRef.current.filter(s_0 => s_0.id !== id);
    setPendingShots(pendingShotsRef.current);
    return shot;
  }, []);
  const commitPendingShot = useCallback(async (id_0: number) => {
    const shot_0 = removePendingShot(id_0);
    if (!shot_0) return;
    try {
      await onCaptureRef.current(shot_0.file, shot_0.studioMask ? {
        studioMask: shot_0.studioMask
      } : undefined);
    } catch (err) {
      console.error("Error handling captured image:", err);
      // A toast, not setError: the camera may already be closed, and the
      // inline error view would hide the rest of the tray.
      toast(isPermissionDeniedError(err) ? permissionDeniedToast : {
        title: "Photo not saved",
        description: "A photo you took couldn't be saved. Please take it again.",
        variant: "error"
      });
    }
  }, [removePendingShot, toast, permissionDeniedToast]);
  const queuePendingShot = useCallback((file: File, studioMask?: Blob) => {
    const id_1 = nextShotIdRef.current++;
    const shot_1 = {
      id: id_1,
      file,
      url: URL.createObjectURL(file),
      studioMask
    };
    pendingShotsRef.current = [...pendingShotsRef.current, shot_1];
    setPendingShots(pendingShotsRef.current);
    pendingTimersRef.current.set(id_1, setTimeout(() => void commitPendingShot(id_1), PENDING_SHOT_DELAY_MS));
  }, [commitPendingShot]);

  // Closing the camera saves anything still waiting rather than dropping it.
  const flushPendingShots = useCallback(() => {
    pendingShotsRef.current.forEach(shot_2 => void commitPendingShot(shot_2.id));
  }, [commitPendingShot]);
  useEffect(() => {
    if (!isOpen) flushPendingShots();
  }, [isOpen, flushPendingShots]);
  useEffect(() => () => flushPendingShots(), [flushPendingShots]);

  // Get available camera devices
  const getCameraDevices = useCallback(async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(device => device.kind === "videoinput");
      setDevices(videoDevices);
      if (videoDevices.length > 0 && !currentDeviceId) {
        // Track the camera the browser actually opened, not the first one
        // listed: on iPad the front camera is listed first, so picking it would
        // restart the stream on the selfie camera.
        const activeDeviceId = streamRef.current?.getVideoTracks()[0]?.getSettings().deviceId;
        setCurrentDeviceId(activeDeviceId || videoDevices[0].deviceId);
      }
    } catch (err_0) {
      console.error("Error enumerating devices:", err_0);
      setError({
        title: "Couldn't find your cameras",
        message: "Tap Try Again. If it keeps happening, close the camera and open it again."
      });
    }
  }, [currentDeviceId]);

  // Check if we have multiple cameras to switch between
  // On mobile, we assume there are always 2 cameras (front and rear)
  const hasMultipleCameras = isMobile ? true : devices.length > 1;

  // Send zoom to the camera, coalescing moves that arrive mid-flight.
  const pushHardwareZoom = useCallback((zoom: number) => {
    hardwareZoomTargetRef.current = zoom;
    if (hardwareZoomBusyRef.current) return;
    const videoTrack = streamRef.current?.getVideoTracks()[0];
    if (!videoTrack) return;
    hardwareZoomBusyRef.current = true;
    void (async () => {
      try {
        while (hardwareZoomTargetRef.current !== null && isHardwareZoomRef.current) {
          const next = hardwareZoomTargetRef.current;
          hardwareZoomTargetRef.current = null;
          await videoTrack.applyConstraints({
            advanced: [{
              zoom: next
            } as any]
          });
        }
      } catch {
        // A track stopped by a camera switch rejects too; only a failure on the
        // live track means hardware zoom doesn't work here.
        if (videoTrack.readyState !== "ended" && videoTrack === streamRef.current?.getVideoTracks()[0]) {
          // Fall back to CSS zoom. Clear the ref synchronously so the next
          // move doesn't retry before React processes the state update.
          isHardwareZoomRef.current = false;
          void videoTrack.applyConstraints({
            advanced: [{
              zoom: 1
            } as any]
          }).catch(() => {});
          setIsHardwareZoom(false);
          setMinZoom(1);
          setMaxZoom(5);
          setZoomLevel(prev => Math.min(Math.max(prev, 1), 5));
        }
      } finally {
        hardwareZoomBusyRef.current = false;
        hardwareZoomTargetRef.current = null;
      }
    })();
  }, []);

  // Set zoom on the camera (hardware) or the preview (CSS fallback). Gestures
  // pass `dial` to bring up the zoom dial and `haptic` to tick on each lens
  // stop they pass, like the iPhone camera.
  const setZoom = useCallback((newZoom: number, {
    dial = false,
    haptic = false
  }: {
    dial?: boolean;
    haptic?: boolean;
  } = {}) => {
    const {
      min,
      max
    } = zoomRangeRef.current;
    const clamped = Math.min(Math.max(newZoom, min), max);
    const prev_0 = zoomLevelRef.current;
    if (haptic && crossedPreset(prev_0, clamped, zoomStopsRef.current) && typeof navigator !== "undefined") {
      navigator.vibrate?.(8);
    }
    zoomLevelRef.current = clamped;
    setZoomLevel(clamped);
    if (dial) {
      setShowZoomIndicator(true);
      if (zoomIndicatorTimeoutRef.current) {
        clearTimeout(zoomIndicatorTimeoutRef.current);
      }
      zoomIndicatorTimeoutRef.current = setTimeout(() => {
        setShowZoomIndicator(false);
      }, 1200);
    }
    if (isHardwareZoomRef.current) pushHardwareZoom(clamped);
  }, [pushHardwareZoom]);
  const cancelZoomAnimation = useCallback(() => {
    if (zoomAnimationRef.current !== null) {
      cancelAnimationFrame(zoomAnimationRef.current);
      zoomAnimationRef.current = null;
    }
  }, []);

  // Glide to a zoom level (lens buttons, keyboard reset). Interpolates in log
  // space so 1x→2x and 2x→4x feel equally fast.
  const animateZoomTo = useCallback((target: number) => {
    cancelZoomAnimation();
    const {
      min: min_0,
      max: max_0
    } = zoomRangeRef.current;
    const to = Math.min(Math.max(target, min_0), max_0);
    const from = zoomLevelRef.current;
    if (Math.abs(to - from) < 0.001) return;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / ZOOM_ANIMATION_MS, 1);
      const eased = 1 - (1 - t) ** 3;
      setZoom(from * (to / from) ** eased);
      zoomAnimationRef.current = t < 1 ? requestAnimationFrame(step) : null;
    };
    zoomAnimationRef.current = requestAnimationFrame(step);
  }, [cancelZoomAnimation, setZoom]);

  // A hand on the zoom always wins over a glide in progress.
  const zoomByGesture = useCallback((newZoom_0: number) => {
    cancelZoomAnimation();
    setZoom(newZoom_0, {
      dial: true,
      haptic: true
    });
  }, [cancelZoomAnimation, setZoom]);

  // Start camera stream
  const startCamera = useCallback(async () => {
    const requestId = ++startRequestRef.current;
    const isStale = () => requestId !== startRequestRef.current;
    try {
      setError(null);
      setIsRequestingAccess(true);
      cancelZoomAnimation();
      // New stream — invalidate any capture still waiting on toBlob.
      captureGenerationRef.current += 1;

      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError({
          title: "This browser can't use the camera",
          message: "Open Decoda in Safari or Chrome to take photos."
        });
        setIsRequestingAccess(false);
        return;
      }

      // Stop any existing stream completely before starting new one
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        streamRef.current = null;
      }
      activeCameraKeyRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // Small delay to ensure stream is fully stopped
      await new Promise(resolve => setTimeout(resolve, 100));
      if (isStale()) return;

      // Determine video constraints
      // On mobile, use facingMode to switch between front/rear cameras
      // On desktop, use deviceId once one is chosen. Until then, prefer
      // defaultFacingMode: tablets wider than the mobile breakpoint take this
      // path, and without a hint iPad Safari opens the front camera. `ideal`
      // falls back to the default webcam on machines that have only one.
      // Use ref to get the latest facingMode value immediately
      const currentFacingMode = facingModeRef.current;
      const useFacingConstraint = isMobile || switchByFacingRef.current;
      // Request the highest resolution the device supports. Without explicit
      // width/height the browser defaults to a low resolution (often 640x480),
      // so we ask for an oversized `ideal` and let the browser clamp down to
      // the device maximum. Use a square ideal so we don't bias the browser
      // toward a particular aspect ratio (an asymmetric ideal like 4096x2160
      // can force a cropped 16:9/1.9:1 mode on natively-4:3 sensors and lose
      // field of view); this preserves the sensor's native ratio in either
      // orientation.
      const resolution = {
        width: {
          ideal: 4096
        },
        height: {
          ideal: 4096
        }
      };
      const constraints: MediaStreamConstraints = {
        video: useFacingConstraint ? {
          ...resolution,
          facingMode: {
            exact: currentFacingMode
          }
        } : currentDeviceId ? {
          ...resolution,
          deviceId: {
            exact: currentDeviceId
          }
        } : {
          ...resolution,
          facingMode: {
            ideal: defaultFacingMode
          }
        },
        audio: false
      };

      // Request camera access - this will trigger permission prompt if needed
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (isStale()) {
        stream.getTracks().forEach(track_0 => track_0.stop());
        return;
      }
      streamRef.current = stream;
      activeCameraKeyRef.current = useFacingConstraint ? currentFacingMode : stream.getVideoTracks()[0]?.getSettings().deviceId || currentDeviceId;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for video to be ready
        await new Promise<void>(resolve_0 => {
          if (videoRef.current) {
            const handleLoadedMetadata = () => {
              if (videoRef.current) {
                setVideoDimensions({
                  width: videoRef.current.videoWidth,
                  height: videoRef.current.videoHeight
                });
                videoRef.current.play().catch(err_2 => {
                  console.error("Error playing video:", err_2);
                });
              }
              resolve_0();
            };

            // If metadata is already loaded
            if (videoRef.current.readyState >= 1) {
              setVideoDimensions({
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              });
              videoRef.current.play().catch(err_3 => {
                console.error("Error playing video:", err_3);
              });
              resolve_0();
            } else {
              videoRef.current.onloadedmetadata = handleLoadedMetadata;
            }
          } else {
            resolve_0();
          }
        });
      }

      // Detect if this is a front camera by checking the track's settings
      const videoTrack_0 = stream.getVideoTracks()[0];
      if (videoTrack_0) {
        // The OS took the camera away (another app, a call, the app being
        // backgrounded). Drop the dead stream and offer a restart instead of
        // leaving a frozen preview.
        videoTrack_0.addEventListener?.("ended", () => {
          if (streamRef.current !== stream) return;
          stream.getTracks().forEach(track_1 => track_1.stop());
          streamRef.current = null;
          activeCameraKeyRef.current = null;
          setHasPermission(false);
          setError({
            title: "The camera stopped",
            message: "Another app or a call may have taken it. Tap Try Again to restart it."
          });
          reportCameraInterrupted("camera_window");
        });
        const settings = videoTrack_0.getSettings();
        const detectedFacingMode = settings.facingMode;
        // Use detected facingMode if available, otherwise fall back to our requested state
        const effectiveFacingMode = detectedFacingMode || currentFacingMode;
        cameraReportsFacingRef.current = detectedFacingMode === "user" || detectedFacingMode === "environment";
        setIsFrontCamera(effectiveFacingMode === "user");

        // Probe hardware zoom capability
        try {
          const capabilities = (videoTrack_0 as any).getCapabilities?.();
          // Some webcams report zoom in raw units (e.g. 100-500) rather than
          // as a 1x-based factor; those get the CSS zoom so the lens buttons
          // and readout stay meaningful and capture crops what's on screen.
          if (capabilities?.zoom && (capabilities.zoom.min ?? 1) <= 1) {
            isHardwareZoomRef.current = true;
            setIsHardwareZoom(true);
            setMinZoom(capabilities.zoom.min ?? 1);
            setMaxZoom(capabilities.zoom.max ?? 10);
          } else {
            isHardwareZoomRef.current = false;
            setIsHardwareZoom(false);
            setMinZoom(1);
            setMaxZoom(5); // CSS fallback max
          }
        } catch {
          isHardwareZoomRef.current = false;
          setIsHardwareZoom(false);
          setMinZoom(1);
          setMaxZoom(5);
        }
      } else {
        // Fallback: use our requested facingMode
        setIsFrontCamera(currentFacingMode === "user");
        isHardwareZoomRef.current = false;
        setIsHardwareZoom(false);
        setMinZoom(1);
        setMaxZoom(5);
      }

      // Reset zoom for new camera
      setZoomLevel(1);
      setHasPermission(true);
      setIsRequestingAccess(false);
      await getCameraDevices();
    } catch (err_1: any) {
      // A newer start or a close owns the UI now.
      if (isStale()) return;
      setIsRequestingAccess(false);

      // A front/back flip asked for a side no camera faces. Go back to
      // stepping through the device list, starting with the next camera.
      if (err_1?.name === "OverconstrainedError" && switchByFacingRef.current && !isMobile) {
        switchByFacingRef.current = false;
        facingSwitchUnavailableRef.current = true;
        const list = devicesRef.current;
        const index = list.findIndex(d => d.deviceId === currentDeviceId);
        if (list.length > 1) {
          setCurrentDeviceId(list[(index + 1) % list.length].deviceId);
        }
        // Flip the requested side back and reopen a camera by deviceId.
        const restoredFacing = facingModeRef.current === "user" ? "environment" : "user";
        facingModeRef.current = restoredFacing;
        setFacingMode(restoredFacing);
        setFacingSwitchCount(count => count + 1);
        return;
      }
      console.error("Error accessing camera:", err_1);
      void reportCameraStartFailed("camera_window", err_1, {
        is_mobile: isMobile
      });
      if (err_1.name === "NotAllowedError" || err_1.name === "PermissionDeniedError") {
        setError({
          title: "Camera access is blocked",
          message: "Allow camera access for Decoda, then tap Try Again. If it's still blocked, reload the app.",
          blocked: true
        });
      } else if (err_1.name === "NotFoundError" || err_1.name === "DevicesNotFoundError") {
        setError({
          title: "No camera found",
          message: "Use a device with a camera, or connect one and try again."
        });
      } else if (err_1.name === "NotReadableError" || err_1.name === "TrackStartError") {
        setError({
          title: "The camera is in use",
          message: "Close other apps or tabs using the camera, then tap Try Again."
        });
      } else {
        setError({
          title: "Couldn't start the camera",
          message: err_1.message || "Check that camera access is allowed and try again."
        });
      }
      setHasPermission(false);
    }
  }, [currentDeviceId, facingMode, isMobile, getCameraDevices, defaultFacingMode, cancelZoomAnimation]);
  const startCameraRef = useRef(startCamera);
  startCameraRef.current = startCamera;

  // Stop camera stream
  const stopCamera = useCallback(() => {
    // Invalidate any start still waiting on getUserMedia.
    startRequestRef.current += 1;
    activeCameraKeyRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track_2 => track_2.stop());
      streamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track_3 => track_3.stop());
      audioStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setVideoDimensions(null);
  }, []);

  // Handle camera switch (cycle through devices)
  const handleSwitchCamera = useCallback(() => {
    // Switching cameras changes the field of view, orientation, and resets
    // zoom, so a session ghost from the previous camera can no longer line up.
    // Drop it; a caller-provided reference image stays and is re-oriented by the
    // overlay transform. Bump the generation synchronously here (not just later
    // in the startCamera effect) so a capture still waiting on toBlob doesn't
    // reinstall the previous camera's frame in the gap before the restart.
    captureGenerationRef.current += 1;
    clearGhost();
    if (isMobile || cameraReportsFacingRef.current && !facingSwitchUnavailableRef.current) {
      // Toggle between front (user) and rear (environment) cameras. On a
      // tablet, Safari then opens the multi-lens camera for the back, so the
      // ultra-wide and telephoto stay reachable through zoom.
      switchByFacingRef.current = true;
      const currentFacing = isMobile ? facingModeRef.current : isFrontCamera ? "user" : "environment";
      const newFacingMode = currentFacing === "user" ? "environment" : "user";
      facingModeRef.current = newFacingMode;
      setFacingMode(newFacingMode);
      setFacingSwitchCount(count_0 => count_0 + 1);
      // The useEffect will handle restarting the camera with the new facingMode
    } else {
      // On desktop, cycle through available devices
      if (devices.length <= 1) return;
      const currentIndex = devices.findIndex(device_0 => device_0.deviceId === currentDeviceId);
      const nextIndex = (currentIndex + 1) % devices.length;
      setCurrentDeviceId(devices[nextIndex].deviceId);
    }
  }, [devices, currentDeviceId, isMobile, isFrontCamera, clearGhost]);

  // Pinch-to-zoom touch handlers
  const handleTouchStart = useCallback((e_0: React.TouchEvent) => {
    if (e_0.touches.length === 2) {
      e_0.preventDefault();
      cancelZoomAnimation();
      const distance = getTouchDistance(e_0.touches[0], e_0.touches[1]);
      gestureStartRef.current = {
        distance,
        zoom: zoomLevelRef.current
      };
      isGesturingRef.current = true;
    } else if (e_0.touches.length === 1) {
      tapTimestampRef.current = Date.now();
      isGesturingRef.current = false;
    }
  }, [cancelZoomAnimation]);
  const handleTouchMove = useCallback((e_1: React.TouchEvent) => {
    if (e_1.touches.length === 2 && gestureStartRef.current) {
      e_1.preventDefault();
      const distance_0 = getTouchDistance(e_1.touches[0], e_1.touches[1]);
      const ratio = distance_0 / gestureStartRef.current.distance;
      zoomByGesture(gestureStartRef.current.zoom * ratio);
    }
  }, [zoomByGesture]);

  // Empty deps is correct: all mutable state is accessed via stable refs
  // (gestureStartRef, isGesturingRef, tapTimestampRef, videoContainerRef,
  // focusRingTimeoutRef, streamRef). React refs don't need to be in deps.
  const handleTouchEnd = useCallback((e_2: React.TouchEvent) => {
    if (gestureStartRef.current) {
      // End of pinch gesture — clear gesture start but keep isGesturingRef
      // true so the second finger lift doesn't falsely trigger tap-to-focus.
      // isGesturingRef resets to false on the next single-finger touchstart.
      gestureStartRef.current = null;
      return;
    }

    // Single tap detection for focus
    if (e_2.changedTouches.length === 1 && !isGesturingRef.current && Date.now() - tapTimestampRef.current < 300) {
      const touch = e_2.changedTouches[0];
      const container = videoContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Show focus ring
      setFocusPoint({
        x,
        y
      });
      setShowFocusRing(true);
      if (focusRingTimeoutRef.current) {
        clearTimeout(focusRingTimeoutRef.current);
      }
      focusRingTimeoutRef.current = setTimeout(() => {
        setShowFocusRing(false);
      }, 800);

      // Attempt hardware focus if available
      if (streamRef.current) {
        const videoTrack_1 = streamRef.current.getVideoTracks()[0];
        if (videoTrack_1) {
          try {
            const capabilities_0 = (videoTrack_1 as any).getCapabilities?.();
            const focusModes = Array.isArray(capabilities_0?.focusMode) ? capabilities_0.focusMode : [];
            if (focusModes.includes("manual") || focusModes.includes("single-shot")) {
              // Convert tap coordinates to normalized [0,1] range
              const pointX = (touch.clientX - rect.left) / rect.width;
              const pointY = (touch.clientY - rect.top) / rect.height;
              // Prefer single-shot (focuses once, then returns to autofocus)
              // over manual (locks focus permanently)
              const focusMode = focusModes.includes("single-shot") ? "single-shot" : "manual";
              void videoTrack_1.applyConstraints({
                advanced: [{
                  focusMode,
                  pointsOfInterest: [{
                    x: pointX,
                    y: pointY
                  }]
                } as any]
              }).catch(() => {
                // Silently fail - focus ring animation still shows as feedback
              });
            }
          } catch {
            // Silently fail - focus ring animation still shows as feedback
          }
        }
      }
    }
  }, []);

  // Capture photo
  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;

    // Snapshot the current stream generation so the async toBlob callback can
    // detect a camera switch/restart that happened while it was pending.
    const captureGeneration = captureGenerationRef.current;

    // Handle CSS fallback zoom: crop center of the frame at native resolution.
    // Set canvas to cropped dimensions to avoid upscaling artifacts.
    // This center-crop matches the CSS scale() preview because the container's
    // aspectRatio is set to match videoDimensions, so object-contain produces
    // no letterboxing and the visible region equals the center 1/zoomLevel.
    if (!isHardwareZoom && zoomLevel > 1) {
      const sw = Math.round(video.videoWidth / zoomLevel);
      const sh = Math.round(video.videoHeight / zoomLevel);
      const sx = Math.round((video.videoWidth - sw) / 2);
      const sy = Math.round((video.videoHeight - sh) / 2);
      canvas.width = sw;
      canvas.height = sh;
      if (isFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      context.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    } else {
      // Hardware zoom or no zoom: draw the full frame
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (isFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // Reset transform for next capture
    context.setTransform(1, 0, 0, 1, 0, 0);

    // Snapshot the frame before any await: with several shots in a row, the
    // next tap redraws this canvas while this shot's mask is being made.
    const blobPromise = new Promise<Blob | null>(resolve_1 => canvas.toBlob(resolve_1, "image/jpeg", 0.95));
    let maskSource: HTMLCanvasElement | null = null;
    if (studioMode) {
      maskSource = document.createElement("canvas");
      maskSource.width = canvas.width;
      maskSource.height = canvas.height;
      maskSource.getContext("2d")?.drawImage(canvas, 0, 0);
    }
    let studioMask_0: Blob | undefined;
    if (maskSource) {
      const segmenter = await getPersonSegmenter();
      if (segmenter) {
        studioMask_0 = (await computePersonMask(segmenter, maskSource)) ?? undefined;
      }
    }
    const blob_0 = await blobPromise;
    if (!blob_0) return;
    // Convert blob to file with timestamp name
    const file_0 = new File([blob_0], `photo-${Date.now()}.jpg`, {
      type: "image/jpeg"
    });
    // Keep the latest shot available as a ghost overlay for lining up a
    // follow-up capture. The blob's field of view corresponds to the
    // current zoom (a CSS-fallback center crop, or the already-zoomed
    // hardware track frame), so record it to scale the overlay relative
    // to the live zoom later. Skip if the modal closed/reopened or the
    // camera changed while toBlob was pending, so a stale blob from a
    // previous stream or session can't resurrect the ghost.
    if (isOpenRef.current && captureGeneration === captureGenerationRef.current) {
      setGhostFromBlob(blob_0, zoomLevel);
    }
    if (allowMultiple) {
      queuePendingShot(file_0, studioMask_0);
      return;
    }
    setIsCapturing(true);
    try {
      await onCapture(file_0, studioMask_0 ? {
        studioMask: studioMask_0
      } : undefined);
      setIsOpen(false);
    } catch (err_4) {
      console.error("Error handling captured image:", err_4);
      setError(isPermissionDeniedError(err_4) ? {
        title: permissionDeniedToast.title,
        message: permissionDeniedToast.description,
        kind: "save"
      } : {
        title: "Photo not saved",
        message: "Something went wrong saving the photo. Take it again.",
        kind: "save"
      });
    } finally {
      setIsCapturing(false);
    }
  }, [onCapture, setIsOpen, isFrontCamera, isHardwareZoom, zoomLevel, allowMultiple, setGhostFromBlob, queuePendingShot, studioMode, permissionDeniedToast]);

  // Start/stop video recording
  const handleStartRecording = useCallback(async () => {
    const videoStream = streamRef.current;
    // A second tap while the mic prompt is up must not start a second recorder.
    if (!videoStream || startingRecordingRef.current) return;
    startingRecordingRef.current = true;

    // Ask for the mic only now. If it's refused or busy (a call, the scribe),
    // record without sound rather than failing.
    let recordingStream = videoStream;
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      if (streamRef.current !== videoStream) {
        audioStream.getTracks().forEach(track_4 => track_4.stop());
        return;
      }
      audioStreamRef.current = audioStream;
      recordingStream = new MediaStream([...videoStream.getVideoTracks(), ...audioStream.getAudioTracks()]);
    } catch (err_5) {
      console.error("Microphone unavailable for recording:", err_5);
      if (streamRef.current !== videoStream) return;
      toast({
        title: "Recording without sound",
        description: "The microphone isn't available, so this video is silent."
      });
    } finally {
      startingRecordingRef.current = false;
    }
    const mimeType = MediaRecorder.isTypeSupported("video/mp4") ? "video/mp4" : MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
    try {
      const recorder = new MediaRecorder(recordingStream, {
        mimeType
      });
      recordingChunksRef.current = [];
      recorder.ondataavailable = e_3 => {
        if (e_3.data.size > 0) recordingChunksRef.current.push(e_3.data);
      };
      recorder.onstop = async () => {
        audioStreamRef.current?.getTracks().forEach(track_6 => track_6.stop());
        audioStreamRef.current = null;
        const blob_1 = new Blob(recordingChunksRef.current, {
          type: mimeType
        });
        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        const file_1 = new File([blob_1], `video-${Date.now()}.${ext}`, {
          type: mimeType
        });
        setIsCapturing(true);
        try {
          await onCapture(file_1);
          // Always close after video — no multiple videos at once
          setIsOpen(false);
        } catch (err_7) {
          console.error("Error handling recorded video:", err_7);
          setError({
            title: "Video not saved",
            message: "Something went wrong saving the video. Record it again.",
            kind: "save"
          });
        } finally {
          setIsCapturing(false);
        }
      };
      recorder.start(1000); // Collect data every second
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev_1 => prev_1 + 1);
      }, 1000);
    } catch (err_6) {
      console.error("Failed to start recording:", err_6);
      audioStreamRef.current?.getTracks().forEach(track_5 => track_5.stop());
      audioStreamRef.current = null;
      setError({
        title: "This browser can't record video",
        message: "You can still take photos. Tap Try Again to go back.",
        kind: "save"
      });
    }
  }, [onCapture, setIsOpen, toast]);
  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  // Handle modal open/close
  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setError(null);
      setIsRequestingAccess(false);
      setZoomLevel(1);
      setShowZoomIndicator(false);
      setFocusPoint(null);
      setShowFocusRing(false);
      // Reset to the default camera (rear unless the caller asked for front)
      if (isMobile) {
        facingModeRef.current = defaultFacingMode;
        setFacingMode(defaultFacingMode);
        setIsFrontCamera(defaultFacingMode === "user");
      }
      // Start camera automatically on mount
      void startCameraRef.current();
    } else {
      // Stop recording if modal closes while recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      setIsRecording(false);
      setRecordingDuration(0);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      stopCamera();
      cancelZoomAnimation();
      setError(null);
      setHasPermission(false);
      setZoomLevel(1);
      setShowZoomIndicator(false);
      setFocusPoint(null);
      setShowFocusRing(false);
      setShowGhost(false);
      setShowGhostPicker(false);
      setGhostViewMode("overlay");
      setIsAligned(false);
      setAlignHint(null);
      clearGhost();
      clearSelectedGhost();
      // Reset camera state
      setCurrentDeviceId(null);
      switchByFacingRef.current = false;
      cameraReportsFacingRef.current = false;
      facingSwitchUnavailableRef.current = false;
      if (isMobile) {
        facingModeRef.current = defaultFacingMode;
        setFacingMode(defaultFacingMode);
        setIsFrontCamera(defaultFacingMode === "user");
      }
    }
    return () => {
      stopCamera();
    };
    // startCamera is read through a ref: its identity changes once the first
    // stream picks a deviceId, and re-running this effect then would stop the
    // camera and open it again on every open.
  }, [isOpen, stopCamera, isMobile, clearGhost, clearSelectedGhost, defaultFacingMode, cancelZoomAnimation]);

  // Cleanup timeout refs on unmount
  useEffect(() => {
    return () => {
      if (zoomAnimationRef.current !== null) {
        cancelAnimationFrame(zoomAnimationRef.current);
      }
      if (zoomIndicatorTimeoutRef.current) {
        clearTimeout(zoomIndicatorTimeoutRef.current);
      }
      if (focusRingTimeoutRef.current) {
        clearTimeout(focusRingTimeoutRef.current);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      // Revoke via the refs only — the component is unmounting, so don't call
      // setState here.
      if (capturedGhostUrlRef.current) {
        URL.revokeObjectURL(capturedGhostUrlRef.current);
        capturedGhostUrlRef.current = null;
      }
      if (selectedGhostUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedGhostUrlRef.current);
      }
      selectedGhostUrlRef.current = null;
    };
  }, []);

  // Restart camera when device or facing mode changes
  useEffect(() => {
    if (!isOpen || !hasPermission) return;
    // On mobile (or a tablet flipping front/back), restart when facingMode
    // changes; on desktop, when deviceId changes. Skip when the open stream
    // already uses it.
    const wanted = isMobile || switchByFacingRef.current ? facingMode : currentDeviceId;
    if (!wanted || wanted === activeCameraKeyRef.current) return;
    void startCamera();
  }, [currentDeviceId, facingMode, facingSwitchCount, hasPermission, isOpen, isMobile, startCamera]);

  // Desktop zoom: scroll or trackpad-pinch over the preview, and Safari's
  // trackpad pinch (gesture events). Bound natively because React's wheel
  // listener is passive and can't stop the page from zooming.
  useEffect(() => {
    const container_0 = videoContainerRef.current;
    if (!isOpen || !hasPermission || !container_0) return;
    const handleWheel = (e_4: WheelEvent) => {
      e_4.preventDefault();
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
      // notch is ~100px, so it gets a gentler rate.
      const delta = e_4.deltaMode === 1 ? e_4.deltaY * 16 : e_4.deltaY;
      const rate = e_4.ctrlKey ? 0.01 : 0.002;
      zoomByGesture(zoomLevelRef.current * Math.exp(-delta * rate));
    };
    container_0.addEventListener("wheel", handleWheel, {
      passive: false
    });

    // Touch devices also fire gesture events during a pinch, which the touch
    // handlers already cover, so only Mac Safari listens for them.
    const listenForGestures = navigator.maxTouchPoints === 0;
    let gestureStartZoom = 1;
    const handleGestureStart = (e_5: Event) => {
      e_5.preventDefault();
      gestureStartZoom = zoomLevelRef.current;
    };
    const handleGestureChange = (e_6: Event) => {
      e_6.preventDefault();
      const scale = (e_6 as Event & {
        scale?: number;
      }).scale ?? 1;
      zoomByGesture(gestureStartZoom * scale);
    };
    if (listenForGestures) {
      container_0.addEventListener("gesturestart", handleGestureStart);
      container_0.addEventListener("gesturechange", handleGestureChange);
    }
    return () => {
      container_0.removeEventListener("wheel", handleWheel);
      if (listenForGestures) {
        container_0.removeEventListener("gesturestart", handleGestureStart);
        container_0.removeEventListener("gesturechange", handleGestureChange);
      }
    };
  }, [isOpen, hasPermission, zoomByGesture]);

  // Keyboard zoom: + / - step, 0 back to 1x.
  useEffect(() => {
    if (!isOpen || !hasPermission || showGhostPicker) return;
    const handleKeyDown = (e_7: KeyboardEvent) => {
      if (e_7.metaKey || e_7.ctrlKey || e_7.altKey) return;
      const target_0 = e_7.target as HTMLElement | null;
      if (target_0 && (target_0.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target_0.tagName))) {
        return;
      }
      if (e_7.key === "+" || e_7.key === "=") {
        e_7.preventDefault();
        zoomByGesture(zoomLevelRef.current * 1.25);
      } else if (e_7.key === "-" || e_7.key === "_") {
        e_7.preventDefault();
        zoomByGesture(zoomLevelRef.current / 1.25);
      } else if (e_7.key === "0") {
        e_7.preventDefault();
        animateZoomTo(1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hasPermission, showGhostPicker, zoomByGesture, animateZoomTo]);

  // Let go of the camera while the app is in the background and take it back
  // on return. iOS cuts a live camera off when the home-screen app is
  // backgrounded, and holding it across that is what left it refusing the
  // camera until the app was closed and reopened.
  useEffect(() => {
    if (!isOpen) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Save what was recorded so far rather than lose it with the camera.
        handleStopRecording();
        stopCamera();
        setHasPermission(false);
      } else if (!streamRef.current) {
        void startCameraRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isOpen, stopCamera, handleStopRecording]);

  // Build video transform: mirror for front camera + CSS fallback zoom
  const videoTransform = [isFrontCamera ? "scaleX(-1)" : "", !isHardwareZoom && zoomLevel > 1 ? `scale(${zoomLevel})` : ""].filter(Boolean).join(" ");

  // Ghost overlay transform. Both a captured ghost and a caller-provided
  // reference cover a field of view of 1/bakedZoom of the sensor (a captured
  // ghost bakes in its capture-time zoom, whether CSS-fallback crop or hardware
  // track frame; a full reference image is effectively zoom 1). To line the
  // overlay up with the live preview's current crop we scale it by
  // zoomLevel / bakedZoom — the ratio holds regardless of hardware vs CSS zoom.
  // Only a full reference image needs mirroring; a captured ghost is already in
  // preview orientation.
  const ghostBakedZoom = ghostIsCaptured ? capturedGhostZoom : 1;
  const ghostZoomScale = zoomLevel / ghostBakedZoom;
  const ghostTransform = [!ghostIsCaptured && isFrontCamera ? "scaleX(-1)" : "", ghostZoomScale !== 1 ? `scale(${ghostZoomScale})` : ""].filter(Boolean).join(" ");

  // Green-when-aligned: detect the chosen comparison photo's pose (loaded via
  // the same-origin proxy so remote gallery photos aren't CORS-blocked), then
  // poll the live frame and light up when the patient's face matches in
  // position, distance, and tilt. Runs on-device (MediaPipe WASM); degrades to
  // no indicator if the model can't load or no face is found.
  const photoGhostActive = showGhost && !!ghostUrl;
  // The face guide gets the same check against its drawn eye targets. A
  // comparison photo is the more specific target, so it wins when both are on.
  const faceGuideAlignActive = showFaceGuide && !photoGhostActive;
  const alignActive = photoGhostActive || faceGuideAlignActive;
  useEffect(() => {
    if (!photoGhostActive || !ghostUrl) {
      setIsAligned(false);
      setAlignHint(null);
      return;
    }
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    void (async () => {
      const landmarker = await getFaceLandmarker();
      if (!landmarker || cancelled) return;
      let refImg: HTMLImageElement;
      try {
        refImg = await loadDetectableImage(ghostUrl);
      } catch {
        return; // reference image failed to load
      }
      if (cancelled) return;
      const target_1 = detectPose(landmarker, refImg, !ghostIsCaptured && isFrontCamera);
      if (!target_1 || cancelled) return;
      intervalId = setInterval(() => {
        const video_0 = videoRef.current;
        if (!video_0 || video_0.readyState < 2 || !video_0.videoWidth) {
          return;
        }
        const livePose = detectPose(landmarker, video_0, isFrontCamera);
        if (!livePose) {
          setIsAligned(false);
          setAlignHint(null);
          return;
        }
        const result = comparePoses(livePose, target_1, isFrontCamera);
        setIsAligned(result.aligned);
        setAlignHint(result.aligned ? null : result.hint);
      }, 250);
    })();
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      setIsAligned(false);
      setAlignHint(null);
    };
  }, [photoGhostActive, ghostUrl, ghostIsCaptured, isFrontCamera]);

  // Face guide: compare the live face, mapped into the on-screen camera pane,
  // to the guide's eye targets laid out in that same pane.
  const cssZoom = !isHardwareZoom && zoomLevel > 1 ? zoomLevel : 1;
  useEffect(() => {
    if (!faceGuideAlignActive) return;
    let cancelled_0 = false;
    let intervalId_0: ReturnType<typeof setInterval> | null = null;
    void (async () => {
      const landmarker_0 = await getFaceLandmarker();
      if (!landmarker_0 || cancelled_0) return;
      intervalId_0 = setInterval(() => {
        const video_1 = videoRef.current;
        if (!video_1 || video_1.readyState < 2 || !video_1.videoWidth || !video_1.clientWidth) {
          return;
        }
        const livePose_0 = detectPose(landmarker_0, video_1, isFrontCamera);
        if (!livePose_0) {
          setIsAligned(false);
          setAlignHint(null);
          return;
        }
        // The video fills its pane, so its box is the pane the guide draws in.
        const pane = {
          width: video_1.clientWidth,
          height: video_1.clientHeight
        };
        const result_0 = comparePoses(poseInPane(livePose_0, {
          width: video_1.videoWidth,
          height: video_1.videoHeight
        }, pane, cssZoom), faceGuideTargetPose(pane.width, pane.height), isFrontCamera);
        setIsAligned(result_0.aligned);
        setAlignHint(result_0.aligned ? null : result_0.hint);
      }, 250);
    })();
    return () => {
      cancelled_0 = true;
      if (intervalId_0) clearInterval(intervalId_0);
      setIsAligned(false);
      setAlignHint(null);
    };
  }, [faceGuideAlignActive, isFrontCamera, cssZoom]);

  // Videos keep their background, so the preview pauses while recording.
  const showBackgroundPreview = studioMode && !isRecording;
  useEffect(() => {
    const maskCanvas = backgroundMaskRef.current;
    if (!showBackgroundPreview || !hasPermission || !maskCanvas) return;
    let cancelled_1 = false;
    let intervalId_1: ReturnType<typeof setInterval> | null = null;
    void (async () => {
      const segmenter_0 = await getPersonSegmenter();
      // The preview shows the backdrop new Studio photos will get (a
      // checkerboard for transparent).
      const studioSettings = await loadStudioSettings();
      const previewFill = previewFillForBackdrop(backdropForNewPhoto(studioSettings), studioSettings);
      if (cancelled_1) return;
      if (!segmenter_0) {
        setBackgroundHint("Studio unavailable on this device");
        return;
      }
      intervalId_1 = setInterval(() => {
        const video_2 = videoRef.current;
        if (!video_2 || video_2.readyState < 2 || !video_2.videoWidth) return;
        const personShare = drawBackgroundMask(segmenter_0, video_2, maskCanvas, PREVIEW_MAX_EDGE, previewFill);
        const found = hasPerson(personShare);
        maskCanvas.style.opacity = found ? "1" : "0";
        setBackgroundHint(found ? null : "No one in frame. Background will be kept");
      }, 100);
    })();
    return () => {
      cancelled_1 = true;
      if (intervalId_1) clearInterval(intervalId_1);
      maskCanvas.style.opacity = "0";
      setBackgroundHint(null);
    };
  }, [showBackgroundPreview, hasPermission]);

  // In full screen, also hide the browser's own toolbars where the browser
  // allows it (not iPhone Safari, which still gets the screen-filling layout).
  // Leaves browser full screen when the camera closes or the option is off.
  useEffect(() => {
    if (!isOpen || !fullScreen) return;
    const root = document.documentElement;
    if (!document.fullscreenElement && root.requestFullscreen) {
      root.requestFullscreen().catch(() => {
        // Not allowed without a recent tap, or not supported: the layout
        // still fills the window.
      });
    }
    return () => {
      if (document.fullscreenElement === root) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [isOpen, fullScreen]);
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className={cx("overflow-hidden", fullScreen ? "inset-0 h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 justify-center rounded-none border-0 bg-black p-0" : "max-w-4xl p-2")}>
        <DialogTitle className="sr-only">
          {title || "Camera Capture"}
        </DialogTitle>
        <div ref={videoContainerRef} className={cx("touch-none relative mx-auto w-full overflow-hidden bg-black", !fullScreen && "rounded-lg")} style={videoDimensions ? {
        aspectRatio: `${videoDimensions.width} / ${videoDimensions.height}`,
        maxHeight: fullScreen ? "100dvh" : "90vh",
        // Cap width to the aspect-implied width at max height so the
        // box always matches the video's aspect (no letterbox) — keeps
        // the grid/face overlays aligned to the frame on any screen.
        maxWidth: `calc(${fullScreen ? "100dvh" : "90vh"} * ${videoDimensions.width} / ${videoDimensions.height})`
      } : {
        aspectRatio: "4 / 3",
        minHeight: "400px"
      }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          {/* Side-by-side reference photo (left half) */}
          {photoGhostActive && ghostViewMode === "sideBySide" && ghostUrl &&
        // eslint-disable-next-line @next/next/no-img-element
        <img src={ghostUrl} alt="" aria-hidden className="absolute left-0 top-0 z-0 h-full w-1/2 border-r border-white/20 object-contain" />}

          {/* Camera pane: live preview + its overlays. In side-by-side mode it
              sits in the right half, beside the reference photo. */}
          <div className={cx("overflow-hidden", photoGhostActive && ghostViewMode === "sideBySide" ? "absolute right-0 top-0 z-0 h-full w-1/2" : "absolute inset-0 z-0")}>
            <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-contain" style={{
            transform: videoTransform || "none",
            transformOrigin: "center center"
          }} />

            {showBackgroundPreview && <canvas ref={backgroundMaskRef} aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-contain opacity-0" style={{
            transform: videoTransform || "none",
            transformOrigin: "center center"
          }} />}

            {/* Ghosting overlay - onion-skin of the reference (overlay mode) */}
            {photoGhostActive && ghostViewMode === "overlay" && ghostUrl &&
          // eslint-disable-next-line @next/next/no-img-element
          <img src={ghostUrl} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-contain" style={{
            opacity: ghostOpacity / 100,
            transform: ghostTransform || "none",
            transformOrigin: "center center"
          }} />}

            {/* Head-and-shoulders face guide (green once the face lines up) */}
            {showFaceGuide && <FaceGuideOverlay aligned={faceGuideAlignActive && isAligned} />}

            {/* Alignment indicator - green ring when aligned. The ring and pill
                sit on video in either theme, so they take the dark theme's
                brighter green. */}
            {alignActive && isAligned && <div className="dark pointer-events-none absolute inset-0 z-10 rounded-lg ring-4 ring-inset ring-status-success-text transition-opacity" />}
            {/* Nudge / aligned pill: the Decoda mark sits beside the nudge and
                spins on each guidance change; when the face lines up the check
                fades in. */}
            {alignActive && (isAligned || alignHint) && <div className="dark pointer-events-none absolute left-1/2 top-16 z-10 -translate-x-1/2">
                <div className={cx("flex items-center gap-1.5 rounded-full px-3 py-1.5 text-caption-strong text-white shadow-lg backdrop-blur-sm transition-colors", isAligned ? "bg-status-success-text text-background" : "bg-black/60")}>
                  <span className="relative flex size-4 items-center justify-center">
                    {/* Logo spins on each new nudge (changing key restarts it) */}
                    {!isAligned &&
                // eslint-disable-next-line @next/next/no-img-element
                <img key={alignHint ?? ""} src="/logos/decoda-favicon.ico" alt="" aria-hidden className="absolute size-4 rounded-[3px] animate-[spin_0.6s_ease-out]" />}
                    {/* Check fades + eases in when aligned (no spin) */}
                    <RiCheckLine className={cx("absolute size-4 transition-all duration-300 ease-out", isAligned ? "scale-100 opacity-100" : "scale-50 opacity-0")} />
                  </span>
                  <span>{isAligned ? "Aligned" : alignHint}</span>
                </div>
              </div>}

            {/* Rule-of-thirds gridlines */}
            {showGrid && <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/3 top-0 h-full w-[3px] bg-white/50" />
                <div className="absolute left-2/3 top-0 h-full w-[3px] bg-white/50" />
                <div className="absolute left-0 top-1/3 h-[3px] w-full bg-white/50" />
                <div className="absolute left-0 top-2/3 h-[3px] w-full bg-white/50" />
              </div>}
          </div>

          {isRequestingAccess ? <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="size-12 animate-spin rounded-full border-[3px] border-white border-t-transparent" />
                </div>
                <p className="text-body text-white">
                  Requesting camera access...
                </p>
                <p className="mt-2 text-caption text-white/70">
                  Please allow camera permissions when prompted
                </p>
              </div>
            </div> : error ? <div role="alert" className="absolute inset-0 flex items-center justify-center overflow-y-auto bg-black/85 p-6">
              <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-white/10">
                  {error.kind === "save" ? <RiErrorWarningLine className="size-7 text-white" /> : <RiCameraOffLine className="size-7 text-white" />}
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-h3 text-white">{error.title}</p>
                  <p className="text-body text-white/75">{error.message}</p>
                </div>
                {error.blocked && isAppleTouchDevice() && <p className="rounded-lg bg-white/10 px-3 py-2 text-caption text-white/85">
                    To stop this happening, open Settings › Apps › Safari
                    (Settings › Safari on older versions) and set Camera to
                    Allow.
                  </p>}
                <div className="flex flex-wrap justify-center gap-3 pt-1">
                  <button onClick={() => void startCamera()} disabled={isRequestingAccess} className="rounded-full bg-white px-6 py-2 text-body-strong text-black transition-opacity hover:opacity-90 disabled:opacity-50">
                    Try Again
                  </button>
                  {/* Browsers remember a refused camera for the life of the
                      page, so Try Again can't recover it. A home-screen app
                      has no reload button, which is why staff had to close
                      and reopen it. Only for that case: a reload also drops
                      a live softphone call, and a home-screen app shows no
                      leave-page prompt. Hidden while photos are saving. */}
                  {error.blocked && pendingShots.length === 0 && !isCapturing && <button onClick={() => window.location.reload()} className="rounded-full border border-white/40 px-6 py-2 text-body-strong text-white transition-colors hover:bg-white/10">
                        Reload app
                      </button>}
                </div>
                <button onClick={() => setIsOpen(false)} className="text-body text-white/60 underline-offset-4 hover:text-white hover:underline">
                  Close
                </button>
              </div>
            </div> : <>
              {/* Header */}
              <div className="absolute left-0 right-0 top-0 bg-gradient-to-b from-black/60 to-transparent p-4">
                <div className="flex items-center justify-between">
                  {/* Close button */}
                  <button onClick={() => setIsOpen(false)} disabled={isCapturing || disabled} className={cx("flex size-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-all", "hover:bg-black/70", (isCapturing || disabled) && "opacity-50")}>
                    <RiCloseLine className="size-6" />
                  </button>

                  {/* Options menu (gridlines, full screen, face guide,
                      comparison photo, switch camera) */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button disabled={isCapturing || disabled} aria-label="Camera options" title="Options" className={cx("flex size-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-all hover:bg-black/70", (isCapturing || disabled) && "opacity-50")}>
                        <RiMore2Line className="size-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="bottom">
                      {/* Gridlines */}
                      <DropdownMenuItem onSelect={e_8 => {
                    e_8.preventDefault();
                    setShowGrid(prev_2 => !prev_2);
                  }}>
                        <RiGridLine className="mr-2 size-4" />
                        Gridlines
                        {showGrid && <RiCheckLine className="ml-auto size-4" />}
                      </DropdownMenuItem>

                      {/* Full screen */}
                      <DropdownMenuItem onSelect={e_9 => {
                    e_9.preventDefault();
                    setFullScreen(prev_3 => !prev_3);
                  }}>
                        <RiFullscreenLine className="mr-2 size-4" />
                        Full screen
                        {fullScreen && <RiCheckLine className="ml-auto size-4" />}
                      </DropdownMenuItem>

                      {/* Face guide */}
                      {enableGhosting && <DropdownMenuItem onSelect={e_10 => {
                    e_10.preventDefault();
                    setShowFaceGuide(prev_4 => !prev_4);
                  }}>
                          <RiUser3Line className="mr-2 size-4" />
                          Face guide
                          {showFaceGuide && <RiCheckLine className="ml-auto size-4" />}
                        </DropdownMenuItem>}

                      {offerStudio && <DropdownMenuItem onSelect={e_11 => {
                    e_11.preventDefault();
                    void setStudioOn(!studioOn);
                  }}>
                          <RiContrast2Line className="mr-2 size-4" />
                          Studio
                          {studioOn && <RiCheckLine className="ml-auto size-4" />}
                        </DropdownMenuItem>}

                      {/* Comparison photo */}
                      {enableGhosting && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>
                            Comparison photo
                          </DropdownMenuLabel>
                        </>}
                      {enableGhosting && !photoGhostActive && <DropdownMenuItem onSelect={() => setShowGhostPicker(true)}>
                          <RiGhostLine className="mr-2 size-4" />
                          Add comparison photo…
                        </DropdownMenuItem>}
                      {enableGhosting && photoGhostActive && <>
                          <DropdownMenuItem ref={overlayItemRef} onKeyDown={e_12 => {
                      if (e_12.key === "ArrowDown") focusGhostStrength(e_12);
                    }} onSelect={e_13 => {
                      e_13.preventDefault();
                      setGhostViewMode(mode => mode === "overlay" ? "hidden" : "overlay");
                    }}>
                            <RiImageLine className="mr-2 size-4" />
                            Overlay
                            {ghostViewMode === "overlay" && <RiCheckLine className="ml-auto size-4" />}
                          </DropdownMenuItem>
                          {ghostViewMode === "overlay" && <div ref={ghostStrengthRef}
                    // Left/Right/Home/End reach the slider; Up/Down
                    // move to the menu items around it.
                    onKeyDownCapture={e_14 => {
                      const next_0 = e_14.key === "ArrowUp" ? overlayItemRef.current : e_14.key === "ArrowDown" ? sideBySideItemRef.current : null;
                      if (!next_0) return;
                      e_14.preventDefault();
                      e_14.stopPropagation();
                      next_0.focus();
                    }} className="flex flex-col gap-2 px-2 py-1.5">
                              <span className="text-caption text-foreground-secondary">
                                Overlay strength
                              </span>
                              <Slider aria-label="Overlay strength" min={10} max={100} step={5} value={[ghostOpacity]} onValueChange={([value]) => setGhostOpacity(value)} showValue={false} />
                            </div>}
                          <DropdownMenuItem ref={sideBySideItemRef} onKeyDown={e_15 => {
                      if (e_15.key === "ArrowUp") focusGhostStrength(e_15);
                    }} onSelect={e_16 => {
                      e_16.preventDefault();
                      setGhostViewMode(mode_0 => mode_0 === "sideBySide" ? "hidden" : "sideBySide");
                    }}>
                            <RiLayoutColumnLine className="mr-2 size-4" />
                            Side by side
                            {ghostViewMode === "sideBySide" && <RiCheckLine className="ml-auto size-4" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setShowGhostPicker(true)}>
                            <RiImageEditLine className="mr-2 size-4" />
                            Change comparison photo…
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => {
                      setShowGhost(false);
                      clearSelectedGhost();
                    }}>
                            <RiCloseLine className="mr-2 size-4" />
                            Remove comparison photo
                          </DropdownMenuItem>
                        </>}

                      {/* Switch camera */}
                      {(isMobile || hasMultipleCameras) && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={handleSwitchCamera}>
                            <RiCameraSwitchLine className="mr-2 size-4" />
                            Switch camera
                          </DropdownMenuItem>
                        </>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {backgroundHint && <div className="pointer-events-none absolute left-1/2 top-28 z-10 -translate-x-1/2">
                  <div className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-caption-strong text-white shadow-lg backdrop-blur-sm">
                    <RiContrast2Line className="size-4" />
                    <span>{backgroundHint}</span>
                  </div>
                </div>}

              {/* Just-taken photos, saved when their countdown runs out */}
              {pendingShots.length > 0 && <div className="absolute bottom-24 right-4 z-20 flex flex-col items-end gap-2">
                  {pendingShots.map(shot_3 => <div key={shot_3.id} className="relative h-20 w-16 overflow-hidden rounded-lg border-2 border-white bg-black shadow-lg sm:h-28 sm:w-20">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={shot_3.url} alt="Captured photo" className="size-full object-cover" />
                      <button type="button" onClick={() => removePendingShot(shot_3.id)} aria-label="Discard photo" title="Discard photo" className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80">
                        <RiCloseLine className="size-4" />
                      </button>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-black/40">
                        <div className="h-full origin-left animate-shrink-x bg-white" />
                      </div>
                    </div>)}
                </div>}

              {/* Tap-to-focus ring */}
              {showFocusRing && focusPoint && <div className="pointer-events-none absolute z-10" style={{
            left: focusPoint.x - 30,
            top: focusPoint.y - 30,
            width: 60,
            height: 60
          }}>
                  <div className="size-full animate-focusRing rounded-full border-2 border-status-warning-text" />
                </div>}

              {/* Recording duration indicator */}
              {isRecording && <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2">
                  <div className="flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 backdrop-blur-sm">
                    <div className="size-2.5 animate-pulse rounded-full bg-red-500" />
                    <span className="text-body-strong tabular-nums text-white">
                      {Math.floor(recordingDuration / 60)}:
                      {(recordingDuration % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                </div>}

              {/* Bottom controls */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-4 bg-gradient-to-t from-black/60 to-transparent pb-6 pt-12">
                {/* Lens buttons + zoom dial */}
                <CameraZoomControl zoom={zoomLevel} min={minZoom} max={maxZoom} presets={zoomStops} dialVisible={showZoomIndicator} disabled={isCapturing || disabled} onSelectPreset={animateZoomTo} onScrub={zoomByGesture} />
                <div className="flex items-end justify-center gap-10">
                  {/* Photo capture */}
                  <div className="flex flex-col items-center gap-2">
                    <button onClick={handleCapture} disabled={isCapturing || isRecording || disabled} className={cx("relative size-20 rounded-full transition-all", !isCapturing && !isRecording && !disabled && "hover:scale-105 active:scale-95", (isCapturing || isRecording || disabled) && "opacity-50")}>
                      <div className="absolute inset-0 rounded-full border-4 border-white" />
                      <div className="absolute inset-[6px] rounded-full bg-white" />
                      {isCapturing && <div className="absolute inset-0 flex items-center justify-center">
                          <div className="size-8 animate-spin rounded-full border-[3px] border-white border-t-transparent" />
                        </div>}
                    </button>
                    <span className="text-caption-strong text-white/90">
                      Photo
                    </span>
                  </div>

                  {/* Video record */}
                  {allowVideo && <div className="flex flex-col items-center gap-2">
                      <button onClick={isRecording ? handleStopRecording : () => void handleStartRecording()} disabled={isCapturing || disabled} className={cx("relative size-16 rounded-full transition-all", !isCapturing && !disabled && "hover:scale-105 active:scale-95", (isCapturing || disabled) && "opacity-50")}>
                        <div className={cx("absolute inset-0 rounded-full border-4", isRecording ? "border-white" : "border-red-500")} />
                        {isRecording ? <div className="absolute inset-[14px] rounded-md bg-red-500" /> : <div className="absolute inset-[6px] rounded-full bg-red-500" />}
                      </button>
                      <span className="text-caption-strong text-white/90">
                        {isRecording ? "Stop" : "Video"}
                      </span>
                    </div>}
                </div>
              </div>
            </>}

          {/* Hidden canvas for image capture */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Chooser for the comparison photo to ghost/align against */}
        {enableGhosting && <GhostReferencePicker open={showGhostPicker} onOpenChange={setShowGhostPicker} patientId={patientId} onSelectUrl={applySelectedGhost} onSelectFile={file_2 => applySelectedGhost(URL.createObjectURL(file_2))} />}
      </DialogContent>
    </Dialog>;
};
export default CameraCaptureModal;